import CryptoJS from 'crypto-js'
import Vue from "vue";

import {AZURE_ERROR_MULTIPLE_SIGNED_IDENTITIES} from "@/fixtures/Constants";
import { CRYPTO_KEY } from '@/fixtures/Hash'
import OidcPlugin from '@/plugins/OidcPlugin'
import router from "@/router";

Vue.use(OidcPlugin)

const safeDecrypt = (encryptedVal) => {
  try {
    return JSON.parse(CryptoJS.AES.decrypt(encryptedVal, CRYPTO_KEY).toString(CryptoJS.enc.Utf8))
  } catch (err) {
    return null
  }
}

const authority = Vue.$oidc.userManager.settings.authority
const clientId = Vue.$oidc.userManager.settings.client_id
const oidcKey = `oidc.user:${authority}:${clientId}`
const parsedValue = safeDecrypt(localStorage.getItem(oidcKey))

const namespaced = true

const state = {
  isAuthenticated: !!parsedValue,
  accessToken: parsedValue?.access_token || null,
  user: parsedValue || null,
  oidcError: null,
  isLoading: false,
  silentSigninAvailable: false
}

const getters = {
  isAuthenticated: state => state.isAuthenticated,
  accessToken: state => state.accessToken,
  user: state => state.user,
  oidcError: state => state.oidcError,
  isLoading: state => state.isLoading,
  silentSigninAvailable: state => state.silentSigninAvailable
}

const mutations = {
  SET_ACCESS_TOKEN: (state, payload) => {
    state.accessToken = payload
    state.isAuthenticated = payload !== null
  },
  SET_USER: (state, payload) => {
    state.user = payload
  },
  SET_OIDC_ERROR: (state, payload) => {
    state.oidcError = payload
  },
  SET_IS_LOADING: (state, payload) => {
    state.isLoading = payload
  },
  SET_SILENT_SIGNIN_AVAILABLE: (state, payload) => {
    state.silentSigninAvailable = payload
  }
}

const actions = {
  async signin({ commit, dispatch }) {
    commit('SET_IS_LOADING', true)
    try {
      await Vue.$oidc.signin()
    } catch (err) {
      commit('SET_OIDC_ERROR', err)
      throw err
    } finally {
      commit('SET_IS_LOADING', false)
    }
  },
  async signinRedirectCallback({ commit, dispatch }) {
    commit('SET_IS_LOADING', true)
    try {
      await Vue.$oidc.signinCallback()
    } catch (err) {
      Vue.$oidc.userManager.clearStaleState();
      throw err
    } finally {
      commit('SET_IS_LOADING', false)
    }
  },
  async signinSilentCallback({ commit }) {
    commit('SET_IS_LOADING', true)
    try {
      return Vue.$oidc.signinSilentCallback()
    } catch (err) {
      commit('SET_OIDC_ERROR', err)
    } finally {
      commit('SET_IS_LOADING', false)
    }
  },
  async signinSilent({ commit, dispatch }) {
    commit('SET_IS_LOADING', true)
    try {
      const user = await Vue.$oidc.signinSilent()
      if (!user) {
        commit('SET_USER', null)
        return
      }

      commit('SET_USER', user)
      commit('SET_ACCESS_TOKEN', user.access_token)
      commit('SET_SILENT_SIGNIN_AVAILABLE', true)
    } catch (err) {
      commit('SET_USER', null)
      commit('SET_ACCESS_TOKEN', null)
      commit('SET_SILENT_SIGNIN_AVAILABLE', false)

      const { error_description } = err || ''
      const errMultipleIdentity = error_description?.includes(AZURE_ERROR_MULTIPLE_SIGNED_IDENTITIES) || false
      if (errMultipleIdentity) {
        throw err
      } else {
        commit('SET_OIDC_ERROR', err)
      }
    } finally {
      commit('SET_IS_LOADING', false)
    }
  },
  async getUser({ commit }) {
    commit('SET_IS_LOADING', true)
    try {
      const user = await Vue.$oidc.getUser()
      if (!user) {
        commit('SET_USER', null)
        return
      }

      commit('SET_USER', user)
      commit('SET_ACCESS_TOKEN', user.access_token)
    } catch (err) {
      commit('SET_OIDC_ERROR', err)
      throw err
    } finally {
      commit('SET_IS_LOADING', false)
    }
  },
  async silentRenew({ commit }) {
    commit('SET_IS_LOADING', true)
    try {
      const user = await Vue.$oidc.renewToken()
      commit('SET_USER', user)
      commit('SET_ACCESS_TOKEN', user.access_token)
      return user
    } catch (err) {
      commit('SET_USER', null)
      commit('SET_OIDC_ERROR', err)
      commit('SET_ACCESS_TOKEN', null)
      throw err
    } finally {
      commit('SET_IS_LOADING', false)
    }
  },
  async signout({ commit, dispatch, state }) {
    commit('SET_IS_LOADING', true)

    const performLogout = async () => {
      await dispatch('Auth/logout', null, { root: true })
      localStorage.removeItem(oidcKey)
      await router.push({ name: 'Login', replace: true })
    }

    try {
      await dispatch('getUser')
        .then(async () => {
          const user = state.user
          if (user) {
            const { profile: { login_hint } } = user
            await dispatch('Auth/logout', null, { root: true })
            await Vue.$oidc.userManager.clearStaleState()
            await Vue.$oidc.userManager.signoutRedirect({
              extraQueryParams: {
                logout_hint: login_hint
              }
            })
          } else {
            await performLogout()
          }
        })
        .catch(async (err) => {
          await performLogout()
        })
    } catch (err) {
      commit('SET_OIDC_ERROR', err)
      throw err
    } finally {
      commit('SET_IS_LOADING', false)
    }
  }
}

export default { namespaced, state, getters, mutations, actions }
